import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useEmblaCarousel from 'embla-carousel-react';
import { useDrag } from '@use-gesture/react';
import { IconArrowLeft, IconBook, IconCheck } from '@tabler/icons-react';
import { Button } from '@/components/Button';
import { Card } from '@/features/card/Card';
import { Skeleton } from '@/components/Skeleton';
import { ProgressBar } from '@/components/ProgressBar';
import { api } from '@/api/endpoints';
import { ApiException } from '@/api/client';
import { useAuth } from '@/store/auth';
import { useFeed } from '@/store/feed';
import { useUI } from '@/store/ui';
import { haptic, useBackButton } from '@/lib/telegram';
import { shareCard } from '@/lib/shareCard';
import { ENV } from '@/lib/env';
import type { ContentCard, BookCardsResponse } from '@/api/types';

export default function Book() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const backFromShelf = (location.state as { from?: string } | null)?.from === '/shelf';
  const showToast = useUI(s => s.showToast);
  const myRefCode = useAuth(s => s.user?.ref_code ?? null);
  const feedMeta = useFeed(s => s.meta);
  const setSeenProgress = useFeed(s => s.setSeenProgress);
  const cardsSeen = feedMeta?.daily_progress?.cards_seen ?? 0;
  const dailyLimit = feedMeta?.daily_progress?.limit ?? 30;
  const [data, setData] = useState<BookCardsResponse['data'] | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    void api.books.cards(id)
      .then(({ data }) => setData(data))
      .catch(() => showToast('error', 'error'));
  }, [id, showToast]);

  // Назад: на полку (если пришли с полки) или в ленту — общий обработчик для
  // видимой кнопки и для свайпа влево.
  const goBack = () => { haptic.light(); nav(backFromShelf ? '/shelf' : '/'); };
  useBackButton(() => nav(-1), [nav]);

  const cards = data?.cards ?? [];
  // Индекс карточки для баннера (на финальном слайде клампим к последней).
  const cursor = Math.min(selectedIndex, Math.max(0, cards.length - 1));

  // --- Embla (вертикаль): слайды = карточки книги + финальный экран «прочитано» ---
  const [emblaRef, emblaApi] = useEmblaCarousel({ axis: 'y', skipSnaps: false, dragThreshold: 12 });
  const lastIndexRef = useRef(0);

  /** Отметить покинутую карточку seen + лимит (книга — конечный набор, без префетча). */
  const handleLeave = async (leftCard: ContentCard) => {
    const alreadySeen = useFeed.getState().hasSeen(leftCard.id);
    if (alreadySeen) return;
    try {
      const { data: seenData } = await api.feed.seen(leftCard.id);
      useFeed.getState().markSeen(leftCard.id);
      setSeenProgress(seenData.cards_seen, seenData.limit);
      if (seenData.daily_limit_reached) { nav('/limit'); return; }
    } catch (e) {
      if (e instanceof ApiException && e.status === 429) { nav('/limit'); return; }
      showToast(t('errors.network'), 'error');
    }
  };

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      const idx = emblaApi.selectedScrollSnap();
      const prev = lastIndexRef.current;
      lastIndexRef.current = idx;
      setSelectedIndex(idx);
      if (idx === prev) return;
      haptic.light();
      // Вперёд — карточка, которую покинули (включая последнюю при уходе на финал), помечается seen.
      if (idx > prev) {
        const leftCard = cards[prev];
        if (leftCard) void handleLeave(leftCard);
      }
    };
    emblaApi.on('select', onSelect);
    return () => { emblaApi.off('select', onSelect); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emblaApi, cards]);

  // Карточки приходят одним запросом — reInit когда набор слайдов готов/изменился.
  useEffect(() => { emblaApi?.reInit(); }, [emblaApi, cards.length]);

  // --- Left-свайп: назад (независимый горизонтальный жест) ---
  const bindLeft = useDrag(({ last, movement: [mx, my], velocity: [vx], canceled }) => {
    if (last && !canceled && mx < -80 && Math.abs(mx) > Math.abs(my) && (vx > 0.5 || mx < -140)) {
      goBack();
    }
  }, { axis: 'x', filterTaps: true });

  const onAction = async (card: ContentCard, action: 'like' | 'dislike' | 'bookmark' | 'share') => {
    haptic.light();
    // Оптимистично применяем сразу, при ошибке — откатываем.
    setData(prev => {
      if (!prev) return prev;
      return { ...prev, cards: prev.cards.map(c => c.id !== card.id ? c : updateCard(c, action)) };
    });
    if (action === 'share') {
      const startParam = `card_${card.id}${myRefCode ? `__ref_${myRefCode}` : ''}`;
      const url = `https://t.me/${ENV.BOT_USERNAME}?startapp=${startParam}`;
      const text = t('feed.share_text', {
        quote: card.text ?? '',
        author: card.book.author,
        title: card.book.title,
        url,
      });
      void shareCard(card.id, { url, text });
    }
    try {
      await api.cards.toggle(card.id, action);
    } catch {
      setData(prev => {
        if (!prev) return prev;
        return { ...prev, cards: prev.cards.map(c => c.id !== card.id ? c : updateCard(c, action)) };
      });
      showToast(t('errors.generic'), 'error');
    }
  };

  if (!data) {
    return <div className="p-4"><Skeleton height={200} /></div>;
  }

  return (
    <div className="flex-1 flex flex-col bg-bg min-h-0" {...bindLeft()} style={{ touchAction: 'pan-y' }}>
      <header className="px-4 pt-3 flex items-center gap-3">
        <button
          onClick={goBack}
          className="flex items-center gap-1 text-xs text-text-muted hover:text-text rounded-pill bg-surface px-2.5 py-1 shrink-0"
          aria-label={backFromShelf ? t('book.back_to_shelf') : t('book.back_to_feed')}
        >
          <IconArrowLeft size={14} />
          <span>{backFromShelf ? t('book.back_to_shelf') : t('book.back_to_feed')}</span>
        </button>
        <ProgressBar value={cardsSeen / dailyLimit} className="flex-1" />
        <div className="text-xs text-text-muted whitespace-nowrap">
          <span className="text-text font-medium">{t('feed.limit_left', { n: cardsSeen, max: dailyLimit })}</span>
        </div>
      </header>
      <div className="mx-4 my-2 rounded-xl bg-accent/10 px-3 py-2 flex items-start gap-2">
        <IconBook size={16} className="text-accent shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wider text-accent/80">{t('book.banner_eyebrow', { defaultValue: 'Лента книги' })}</div>
          <div className="text-sm font-medium text-text truncate">{data.book.title}</div>
          {data.book.author && (
            <div className="text-[11px] text-text-muted truncate">{data.book.author}</div>
          )}
        </div>
        <div className="text-[11px] text-text-muted whitespace-nowrap mt-0.5">{cursor + 1} / {cards.length}</div>
      </div>

      {/* Embla viewport: карточки книги + финальный слайд «Книга прочитана» */}
      <div className="flex-1 min-h-0 overflow-hidden" ref={emblaRef}>
        <div className="h-full flex flex-col">
          {cards.map((c) => (
            <div key={c.id} className="flex-[0_0_100%] min-h-0 flex flex-col">
              <Card
                card={c}
                onLike={() => onAction(c, 'like')}
                onDislike={() => onAction(c, 'dislike')}
                onBookmark={() => onAction(c, 'bookmark')}
                onShare={() => onAction(c, 'share')}
                onComments={() => nav(`/card/${c.id}/comments`)}
              />
            </div>
          ))}
          {/* Финальный слайд — долистал последнюю карточку. */}
          <div key="__finish" className="flex-[0_0_100%] min-h-0 flex flex-col items-center justify-center text-center px-6 gap-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-pill bg-dark text-text-inverse grid place-items-center">
                <IconBook size={36} />
              </div>
              <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-pill bg-accent text-text-inverse grid place-items-center border-2 border-bg">
                <IconCheck size={14} stroke={3} />
              </div>
            </div>
            <h1 className="font-content text-[28px]">{t('book.finished_title')}</h1>
            <p className="text-text-muted max-w-sm">
              {t('book.finished_sub', { title: data.book.title })}
            </p>
            <div className="w-full max-w-sm flex flex-col gap-2 mt-2">
              <Button block onClick={() => { haptic.light(); nav('/'); }}>
                {t('book.finished_cta_feed')}
              </Button>
              <Button block variant="secondary" onClick={() => { haptic.light(); nav('/shelf'); }}>
                {t('book.finished_cta_shelf')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function updateCard(c: ContentCard, action: 'like' | 'dislike' | 'bookmark' | 'share'): ContentCard {
  const ua = { ...c.user_actions };
  const stats = { ...c.stats };
  if (action === 'like') {
    const next = !ua.liked;
    ua.liked = next;
    stats.likes += next ? 1 : -1;
    if (next && ua.disliked) { ua.disliked = false; stats.dislikes -= 1; }
  } else if (action === 'dislike') {
    const next = !ua.disliked;
    ua.disliked = next;
    stats.dislikes += next ? 1 : -1;
    if (next && ua.liked) { ua.liked = false; stats.likes -= 1; }
  } else if (action === 'bookmark') {
    const next = !ua.bookmarked;
    ua.bookmarked = next;
    stats.bookmarks += next ? 1 : -1;
  } else {
    stats.shares += 1;
  }
  return { ...c, user_actions: ua, stats };
}
